<template>
	<v-main>
		<v-container fluid>
			<FormPage title="IVA estera: adesione OSS, aliquote e soglie USA" :back-label="backLabel" save-label="Salva" :save-disabled="saving || !loaded" @back="back" @save="salva">
				<v-row dense>
					<v-col cols="12" lg="5">
						<SectionCard title="Adesione all’OSS" icon="mdi-earth" class="fs-card">
							<v-text-field
								v-model="foreign.ossFrom"
								type="month"
								label="Mese di adesione"
								outlined
								dense
								clearable
								hint="Da questo mese le vendite B2C UE si dichiarano con l’IVA del Paese del cliente. Vuoto = nessuna adesione"
								persistent-hint
								class="mb-4"
							/>
							<v-switch v-model="foreign.applyToReport" color="primary" inset dense hide-details label="Usa i regimi esteri anche nel report finanziario" />
							<div class="fs-note mt-2">
								Export extra-UE e vendite a clienti UE con partita IVA vanno nel box «0% non imponibile», le vendite OSS (dopo l’adesione) nel box OSS fuori dalla liquidazione italiana. Vale per i
								mesi calcolati da ora: per quelli già fotografati usa «Aggiorna» nel report finanziario.
							</div>
						</SectionCard>

						<SectionCard title="Aliquote dei Paesi UE" icon="mdi-percent-outline" :subtitle="`tabella aggiornata al ${formatDate(tables.updated)}`" no-padding class="mt-2 fs-card">
							<template #actions>
								<v-btn small text color="primary" @click="showTable = !showTable">{{ showTable ? 'Nascondi' : 'Mostra' }} la tabella</v-btn>
							</template>
							<v-simple-table v-if="showTable" dense class="fs-table">
								<thead>
									<tr>
										<th>Paese</th>
										<th v-for="c in tables.classes" :key="c.value" class="text-right">{{ c.text }}</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="c in tables.eu" :key="c.country">
										<td>
											<span class="fs-code">{{ c.country }}</span> {{ c.name }}
										</td>
										<td v-for="k in tables.classes" :key="k.value" class="text-right">{{ pct(c.classes[k.value]) }}</td>
									</tr>
								</tbody>
							</v-simple-table>
							<div class="fs-note pa-3">
								La classe è quella dell’aliquota italiana della riga: 22% ordinaria, 10% ridotta, 5% ridotta minima, 4% super ridotta. Le aliquote ridotte del Paese dipendono dalla merce: se per i
								tuoi prodotti è un’altra, correggila qui sotto.
							</div>
						</SectionCard>
					</v-col>

					<v-col cols="12" lg="7">
						<SectionCard title="Correzioni delle aliquote" icon="mdi-pencil-outline" subtitle="vincono sulla tabella dal mese indicato" no-padding class="fs-card">
							<v-simple-table dense>
								<thead>
									<tr>
										<th>Paese</th>
										<th>Classe</th>
										<th style="width: 110px">Aliquota</th>
										<th style="width: 150px">Dal mese</th>
										<th style="width: 44px"></th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(o, i) in foreign.overrides" :key="i">
										<td><v-autocomplete v-model="o.country" :items="countryItems" dense hide-details single-line placeholder="Paese" /></td>
										<td><v-select v-model="o.rateClass" :items="tables.classes" dense hide-details single-line /></td>
										<td><v-text-field v-model="o.rate" type="number" min="0" max="30" step="0.1" dense hide-details single-line suffix="%" :placeholder="defaultPct(o)" /></td>
										<td><v-text-field v-model="o.validFrom" type="month" dense hide-details single-line /></td>
										<td>
											<v-btn icon small title="Togli" @click="foreign.overrides.splice(i, 1)"><v-icon small>mdi-close</v-icon></v-btn>
										</td>
									</tr>
									<tr v-if="!foreign.overrides.length">
										<td colspan="5" class="grey--text py-3">Nessuna correzione: si usano le aliquote della tabella.</td>
									</tr>
								</tbody>
							</v-simple-table>
							<div class="pa-2">
								<v-btn text small color="primary" @click="foreign.overrides.push({ country: null, rateClass: 'reduced', rate: '', validFrom: '' })"
									><v-icon small left>mdi-plus</v-icon>Aggiungi correzione</v-btn
								>
							</div>
						</SectionCard>

						<SectionCard title="Soglie sales tax USA" icon="mdi-flag-variant-outline" subtitle="valori di partenza da verificare con il consulente" no-padding class="mt-2 fs-card">
							<v-simple-table dense fixed-header height="520px">
								<thead>
									<tr>
										<th>Stato</th>
										<th style="width: 120px">Soglia USD</th>
										<th style="width: 100px">Ordini</th>
										<th style="width: 70px" title="Servono entrambe le soglie">E</th>
										<th style="width: 130px">Misura</th>
										<th style="width: 90px" title="Le vendite tramite marketplace contano per la soglia">Marketplace</th>
										<th style="width: 90px">Registrato</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="s in usRows" :key="s.state" :class="{ 'grey--text': s.noTax }">
										<td>
											<span class="fs-code">{{ s.state }}</span> {{ s.name }}
										</td>
										<template v-if="s.noTax">
											<td colspan="5" class="fs-note">Nessuna sales tax statale</td>
										</template>
										<template v-else>
											<td><v-text-field v-model="s.amount" type="number" min="0" dense hide-details single-line /></td>
											<td><v-text-field v-model="s.transactions" type="number" min="0" dense hide-details single-line placeholder="—" /></td>
											<td><v-simple-checkbox v-model="s.and" :ripple="false" /></td>
											<td><v-select v-model="s.period" :items="PERIODS" dense hide-details single-line /></td>
											<td><v-simple-checkbox v-model="s.includeMarketplace" :ripple="false" /></td>
										</template>
										<td><v-simple-checkbox v-model="s.registered" :ripple="false" :disabled="s.noTax" /></td>
									</tr>
								</tbody>
							</v-simple-table>
							<div class="fs-note pa-3">Registrato = hai già la registrazione per la sales tax nello Stato: il report non ti avvisa più per quello Stato.</div>
						</SectionCard>
					</v-col>
				</v-row>
			</FormPage>
		</v-container>
	</v-main>
</template>

<script>
import { mapActions } from 'vuex';
import FormPage from '@/components/core/FormPage.vue';
import SectionCard from '@/components/core/SectionCard.vue';
import { returnInfo, goBackWithResult } from '@/components/core/pageReturn';

const PERIODS = [
	{ value: 'calendar', text: 'Anno solare' },
	{ value: 'rolling', text: 'Ultimi 12 mesi' },
];

const empty = v => v === '' || v === null || v === undefined;

export default {
	name: 'FinanceForeignSettings',
	components: { FormPage, SectionCard },
	data() {
		return {
			PERIODS,
			ret: returnInfo(this.$route),
			foreign: { ossFrom: '', applyToReport: true, overrides: [] },
			tables: { eu: [], classes: [], us: [], updated: '' },
			usRows: [],
			showTable: false,
			loaded: false,
			saving: false,
		};
	},
	computed: {
		backLabel() {
			return this.ret ? this.ret.label : 'Torna al report IVA estera';
		},
		countryItems() {
			return this.tables.eu.map(c => ({ value: c.country, text: `${c.country} · ${c.name}` }));
		},
	},
	async mounted() {
		try {
			const res = await this.fetchSettings();
			const f = res.foreign || {};
			this.tables = res.tables;
			this.foreign = {
				ossFrom: f.ossFrom || '',
				applyToReport: f.applyToReport !== false,
				overrides: (f.overrides || []).map(o => ({ ...o, validFrom: o.validFrom || '' })),
			};
			const custom = new Map((f.usNexus || []).map(s => [s.state, s]));
			const registered = new Set(f.usRegistered || []);
			this.usRows = this.tables.us.map(def => {
				const s = { ...def, ...(custom.get(def.state) || {}) };
				return { ...s, amount: empty(s.amount) ? '' : s.amount, transactions: empty(s.transactions) ? '' : s.transactions, registered: registered.has(def.state) };
			});
			this.loaded = true;
		} catch (err) {
			this.notify('error', err.message);
		}
	},
	methods: {
		...mapActions({ fetchSettings: 'finance/fetchForeignSettings', saveSettings: 'finance/saveForeignSettings' }),
		pct: v => (empty(v) ? '—' : `${String(v).replace('.', ',')}%`),
		formatDate: d => (d ? d.split('-').reverse().join('/') : ''),
		defaultPct(o) {
			const c = this.tables.eu.find(x => x.country === o.country);
			return c && o.rateClass ? String(c.classes[o.rateClass]) : '';
		},
		back() {
			if (this.ret) goBackWithResult(this.$router, this.ret, '', null);
			else this.$router.push('/finance/foreign');
		},
		// si salvano solo gli Stati diversi dalla tabella di partenza
		usChanges() {
			const defaults = new Map(this.tables.us.map(s => [s.state, s]));
			const num = v => (empty(v) ? null : Number(v));
			return this.usRows
				.filter(s => !s.noTax)
				.map(s => ({ state: s.state, amount: num(s.amount), transactions: num(s.transactions), and: !!s.and, period: s.period, includeMarketplace: !!s.includeMarketplace }))
				.filter(s => {
					const d = defaults.get(s.state);
					return s.amount !== d.amount || s.transactions !== d.transactions || s.and !== !!d.and || s.period !== d.period || s.includeMarketplace !== !!d.includeMarketplace;
				});
		},
		async salva() {
			this.saving = true;
			try {
				await this.saveSettings({
					ossFrom: this.foreign.ossFrom || null,
					applyToReport: this.foreign.applyToReport,
					overrides: this.foreign.overrides.filter(o => o.country && o.rateClass && !empty(o.rate)).map(o => ({ ...o, rate: Number(o.rate), validFrom: o.validFrom || null })),
					usNexus: this.usChanges(),
					usRegistered: this.usRows.filter(s => s.registered).map(s => s.state),
				});
				this.notify('success', 'Salvato: per i mesi già calcolati usa «Aggiorna» nel report IVA estera');
				this.back();
			} catch (err) {
				const res = err.response;
				this.notify('error', (res && res.data && (res.data.message || (res.data.error && res.data.error[0] && res.data.error[0].msg))) || err.message);
			} finally {
				this.saving = false;
			}
		},
		notify(color, text) {
			this.$store.dispatch('settings/updateSnackbar', { color, text, snackbar: true, mode: 'multi-line', timeout: 5000 });
		},
	},
};
</script>

<style scoped>
.fs-note {
	font-size: 12px;
	color: #5f6b7a;
}
.fs-code {
	font-family: monospace;
	font-size: 12px;
	color: #5f6b7a;
}
/* due card nella stessa colonna: ognuna alta quanto il suo contenuto */
.fs-card {
	height: auto !important;
}
.fs-table {
	max-height: 420px;
	overflow-y: auto;
}
</style>
